import { Box, Grid2, Typography } from "@mui/material";
import {
  JOB_INACTIVE_STATUSES,
  SUMMARY_TILE_TONES,
  TRUCK_UNAVAILABLE_STATUSES,
} from "../../../../constants";
import { JobStatus } from "../../../../enums";
import { JobModel } from "../../../../models";
import { CrewMember, Job, SummaryTileTone, Truck } from "../../../../types";

type SummaryCardProps = {
  label: string;
  value: string | number;
  helperText: string;
  tone: SummaryTileTone;
  onClick?: (anchor: HTMLElement) => void;
};

const SummaryCard = ({
  label,
  value,
  helperText,
  tone,
  onClick,
}: SummaryCardProps) => (
  <Box
    className="dashboard-tile"
    role={onClick ? "button" : undefined}
    tabIndex={onClick ? 0 : undefined}
    onClick={(event) => onClick?.(event.currentTarget)}
    onKeyDown={(event) => {
      if (onClick && (event.key === "Enter" || event.key === " ")) {
        event.preventDefault();
        onClick(event.currentTarget);
      }
    }}
    sx={{
      minHeight: "auto",
      py: 1.5,
      cursor: onClick ? "pointer" : "default",
      backgroundColor: SUMMARY_TILE_TONES[tone].background,
      borderColor: SUMMARY_TILE_TONES[tone].border,
    }}
  >
    <Typography className="dashboard-metric-label">{label}</Typography>
    <Typography
      className="dashboard-kpi-value"
      sx={{ mt: 0.5, color: SUMMARY_TILE_TONES[tone].value }}
    >
      {value}
    </Typography>
    <Typography className="dashboard-helper-text" sx={{ mt: 0.5 }}>
      {helperText}
    </Typography>
  </Box>
);

type CalendarSummaryCardsProps = {
  jobs: Job[];
  trucks: Truck[];
  crew: CrewMember[];
  conflictCount: number;
  isDayView: boolean;
  onConflictsClick: (anchor: HTMLElement) => void;
};

export const CalendarSummaryCards = ({
  jobs,
  trucks,
  crew,
  conflictCount,
  isDayView,
  onConflictsClick,
}: CalendarSummaryCardsProps) => {
  const period = isDayView ? "Day" : "Week";
  const activeEntries = jobs.filter(
    (job) => !JOB_INACTIVE_STATUSES.includes(job.status),
  );
  const activeJobs = activeEntries.filter((job) => !JobModel.isEvent(job));
  const completedCount = activeJobs.filter(
    (job) => job.status === JobStatus.Completed,
  ).length;
  const scheduledTruckIds = new Set(
    activeEntries.flatMap((job) => (job.truckId === null ? [] : [job.truckId])),
  );
  const availableTrucks = trucks.filter(
    (truck) => !TRUCK_UNAVAILABLE_STATUSES.includes(truck.status),
  );
  const scheduledTruckCount = availableTrucks.filter((truck) =>
    scheduledTruckIds.has(truck.id),
  ).length;
  const assignedCrewIds = new Set(
    activeEntries.flatMap((job) => job.crew.map((member) => member.id)),
  );
  const assignedCrewCount = crew.filter((member) =>
    assignedCrewIds.has(member.id),
  ).length;

  const cards: SummaryCardProps[] = [
    {
      label: `Jobs This ${period}`,
      value: activeJobs.length,
      helperText: `${completedCount} completed`,
      tone: "neutral",
    },
    {
      label: "Trucks Scheduled",
      value: `${scheduledTruckCount}/${availableTrucks.length}`,
      helperText: `trucks have jobs this ${period.toLowerCase()}`,
      tone: "info",
    },
    {
      label: "Crew Assigned",
      value: `${assignedCrewCount}/${crew.length}`,
      helperText: `crew members have jobs this ${period.toLowerCase()}`,
      tone: "success",
    },
    {
      label: "Conflicts",
      value: conflictCount,
      helperText: conflictCount ? "jobs need attention" : "No conflicts",
      tone: conflictCount ? "danger" : "neutral",
      onClick: conflictCount ? onConflictsClick : undefined,
    },
  ];

  return (
    <Grid2 container spacing={2} sx={{ mb: 3 }}>
      {cards.map((card) => (
        <Grid2 key={card.label} size={{ xs: 12, sm: 6, lg: 3 }}>
          <SummaryCard {...card} />
        </Grid2>
      ))}
    </Grid2>
  );
};
